import React from 'react';
import { useRecoilValue } from 'recoil';
import { blockCanExpandState, blockContinuationPageCount, blockExpandedState, isReportState } from 'venn-state';
import { useAppPrintMode } from '../../../../print';
import { Icon } from 'venn-ui-kit';
import { cn } from '@/lib/utils';
import { useExpandBlock } from '../../../../hooks/useExpandBlock';

export type ExpandActionPlacement = 'top' | 'bottom';

interface ExpandActionButtonProps {
  icon: string;
  label: string;
  onClick: () => void;
  placement?: ExpandActionPlacement;
}

const BUTTON_BASE_CLASSES =
  'absolute left-1/2 -translate-x-1/2 z-[5] inline-flex items-center gap-1 px-2 h-6 ' +
  'border-0 cursor-pointer pointer-events-auto whitespace-pre ' +
  'uppercase tracking-[1px] !font-roboto !text-[11px] !font-bold !leading-normal !not-italic ' +
  'bg-venn-dark-blue text-venn-white hover:text-venn-white hover:bg-venn-deep-dark-blue active:bg-venn-active-blue';

export const ExpandActionButton = ({ icon, label, onClick, placement = 'bottom' }: ExpandActionButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(BUTTON_BASE_CLASSES, {
      'bottom-0 rounded-t-[4px]': placement === 'bottom',
      'top-0 -translate-y-full rounded-t-[4px]': placement !== 'bottom',
    })}
  >
    <Icon prefix="fal" type={icon} />
    {label}
  </button>
);

interface ExpandBadgeProps {
  id: string;
}

export const ExpandBadge = ({ id }: ExpandBadgeProps) => {
  const { inPrintMode } = useAppPrintMode();
  const isReport = useRecoilValue(isReportState);
  const isExpanded = useRecoilValue(blockExpandedState(id));
  const canExpand = useRecoilValue(blockCanExpandState(id));
  const { expand } = useExpandBlock(id);

  if (!isReport || inPrintMode || isExpanded || !canExpand) return null;

  return <ExpandActionButton icon="arrow-down-from-dotted-line" label="Expand to fit content" onClick={expand} />;
};

export const ContractBadge = ({ id }: { id: string }) => {
  const { inPrintMode } = useAppPrintMode();
  const isReport = useRecoilValue(isReportState);
  const isExpanded = useRecoilValue(blockExpandedState(id));
  const continuationCount = useRecoilValue(blockContinuationPageCount(id));
  const { contract } = useExpandBlock(id);

  if (!isReport || inPrintMode || !isExpanded || continuationCount > 0) return null;

  return <ExpandActionButton icon="arrow-up-to-dotted-line" label="Contract" onClick={contract} />;
};
